import { Model, RepairService } from '../types/BookingTypes';

export interface SEOContent {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  structuredData: object;
}

export const generateSEOUrl = (model: Model): string => {
  const brandName = model.brand || '';
  const modelName = model.name || '';
  
  // Create SEO-friendly path with screen-replacement-price format
  const brandSlug = brandName.toLowerCase().replace(/\s+/g, '-');
  const modelSlug = modelName.toLowerCase().replace(/\s+/g, '-');
  const seoFriendlyPath = `${brandSlug}-${modelSlug}-screen-replacement-price`;
  
  return `/services/${model.id}/${seoFriendlyPath}`;
};

export const generateSEOContent = (model: Model, services: RepairService[], modelId: string): SEOContent => {
  const brandName = model.brand || '';
  const modelName = model.name || '';
  
  // Check if screen/display services are available
  const hasScreenService = services.some(service => 
    service.serviceName.toLowerCase().includes('screen') || 
    service.serviceName.toLowerCase().includes('display')
  );
  
  // Check for other common services
  const hasBatteryService = services.some(service => 
    service.serviceName.toLowerCase().includes('battery')
  );
  
  const hasChargingService = services.some(service => 
    service.serviceName.toLowerCase().includes('charging') ||
    service.serviceName.toLowerCase().includes('port')
  );
  
  let title = '';
  let description = '';
  let keywords = '';
  
  if (hasScreenService) {
    // Target primary keywords like "iPhone 12 screen replacement"
    title = `${brandName} ${modelName} Screen Replacement | Display Repair | Minto`;
    description = `Get professional ${brandName} ${modelName} screen replacement and display repair at your doorstep. Expert ${brandName} ${modelName} screen repair service with warranty. Book now in Mumbai!`;
    keywords = `${brandName} ${modelName} screen replacement, ${brandName} ${modelName} display repair, ${brandName} ${modelName} screen repair, ${brandName} ${modelName} display price, ${modelName} screen replacement, ${modelName} display repair, mobile screen repair, phone display repair`;
  } else if (hasBatteryService) {
    title = `${brandName} ${modelName} Battery Replacement | Mobile Battery Repair | Minto`;
    description = `Professional ${brandName} ${modelName} battery replacement service at your doorstep. Expert ${brandName} ${modelName} battery repair with genuine parts and warranty. Book now!`;
    keywords = `${brandName} ${modelName} battery replacement, ${brandName} ${modelName} battery repair, ${modelName} battery replacement, mobile battery repair, phone battery replacement`;
  } else if (hasChargingService) {
    title = `${brandName} ${modelName} Charging Port Repair | Mobile Charging Issue | Minto`;
    description = `Fix ${brandName} ${modelName} charging port problems at your doorstep. Expert ${brandName} ${modelName} charging port repair service with warranty. Book now!`;
    keywords = `${brandName} ${modelName} charging port repair, ${brandName} ${modelName} charging issue, ${modelName} charging port repair, mobile charging port fix`;
  } else {
    // General repair services
    title = `${brandName} ${modelName} Repair Services | Mobile Repair | Minto`;
    description = `Professional ${brandName} ${modelName} repair services at your doorstep. Battery, charging port, speaker repair and more. Expert technicians with warranty. Book now!`;
    keywords = `${brandName} ${modelName} repair, ${brandName} ${modelName} service, ${modelName} repair, mobile repair, phone repair`;
  }
  
  // Generate SEO-friendly URL path
  const brandSlug = brandName.toLowerCase().replace(/\s+/g, '-');
  const modelSlug = modelName.toLowerCase().replace(/\s+/g, '-');
  const seoFriendlyPath = `${brandSlug}-${modelSlug}-screen-replacement-price`;
  const canonicalUrl = `https://mintorepairs.com/services/${modelId}/${seoFriendlyPath}`;
  
  // Structured data for local business and service
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": canonicalUrl,
        "name": `${brandName} ${modelName} Repair`,
        "description": description,
        "provider": {
          "@type": "LocalBusiness",
          "name": "Minto Mobile Repair",
          "url": "https://mintorepairs.com",
          "telephone": "+91-9876543210",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Mumbai",
            "addressLocality": "Kalyan",
            "addressRegion": "Maharashtra",
            "postalCode": "421301",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "19.2403",
            "longitude": "73.1305"
          },
          "openingHours": "Mo-Su 09:00-21:00",
          "priceRange": "₹₹"
        },
        "areaServed": [
          {
            "@type": "Place",
            "name": "Mumbai"
          },
          {
            "@type": "Place", 
            "name": "Kalyan"
          },
          {
            "@type": "Place",
            "name": "Dombivli"
          }
        ],
        "serviceType": `${brandName} ${modelName} Mobile Repair`,
        "category": "Mobile Phone Repair",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceRange": "₹299-₹999",
          "priceCurrency": "INR",
          "validFrom": new Date().toISOString(),
          "areaServed": {
            "@type": "Place",
            "name": "Mumbai, Kalyan, Dombivli"
          }
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://mintorepairs.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": `${brandName} Repair`,
            "item": `https://mintorepairs.com/brand/${model.brandId}`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${brandName} ${modelName} Repair`,
            "item": canonicalUrl
          }
        ]
      }
    ]
  };
  
  return {
    title,
    description,
    keywords,
    canonicalUrl,
    structuredData
  };
};

export const updatePageSEO = (seoContent: SEOContent) => {
  // Update page title
  document.title = seoContent.title;
  
  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', seoContent.description);
  }
  
  // Update meta keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute('content', seoContent.keywords);
  
  // Update Open Graph meta tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute('content', seoContent.title);
  }
  
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) {
    ogDescription.setAttribute('content', seoContent.description);
  }
  
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) {
    ogUrl.setAttribute('content', seoContent.canonicalUrl);
  }
  
  // Update Twitter meta tags
  const twitterTitle = document.querySelector('meta[property="twitter:title"]');
  if (twitterTitle) {
    twitterTitle.setAttribute('content', seoContent.title);
  }
  
  const twitterDescription = document.querySelector('meta[property="twitter:description"]');
  if (twitterDescription) {
    twitterDescription.setAttribute('content', seoContent.description);
  }
  
  const twitterUrl = document.querySelector('meta[property="twitter:url"]');
  if (twitterUrl) {
    twitterUrl.setAttribute('content', seoContent.canonicalUrl);
  }
  
  // Update canonical URL
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', seoContent.canonicalUrl);
  }
  
  // Add structured data
  const existingScript = document.querySelector('#seo-structured-data');
  if (existingScript) {
    existingScript.remove();
  }
  
  const script = document.createElement('script');
  script.id = 'seo-structured-data';
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(seoContent.structuredData);
  document.head.appendChild(script);
};

export const resetPageSEO = () => {
  // Reset to default title
  document.title = 'Doorstep Mobile Repair in Mumbai | Minto';
  
  // Reset meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', 'Get fast and reliable doorstep mobile repair in Kalyan & Dombivli with Minto. Screen, battery, and more—expert service at your home. Book now!');
  }
  
  // Reset canonical URL
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', 'https://www.mintoservice.com');
  }
  
  // Remove structured data
  const existingScript = document.querySelector('#seo-structured-data');
  if (existingScript) {
    existingScript.remove();
  }
}; 